/**
 * RPMMixer — the layered engine core.
 *
 * Two sets of loops (throttle ON / throttle OFF), each a list of samples
 * recorded at known rpm. All sources run permanently; only gains and playback
 * rates move. Between two neighbouring rpm points we do an equal-power
 * crossfade; throttle crossfades the ON and OFF sets. This mirrors the FMOD
 * layout used by Assetto Corsa and is how real samples will be used later.
 */

export interface LoadedLoop {
  id: string;
  recordedRpm: number;
  buffer: AudioBuffer;
  /** true when synthesized as a fallback */
  procedural: boolean;
  loopStart?: number;
  loopEnd?: number;
}

export interface RPMMixerOptions {
  /** Minimum share of the ON set at zero throttle (0..1). */
  onMixFloor: number;
  /** Automation time constant in seconds. */
  tau: number;
  /**
   * A silent voice's SOURCE is stopped, not just unplugged. Unplugging the gain is enough on a desktop
   * Chromium, which never pulls a node nothing listens to; the car's browser is another build on a
   * weaker chip and the three Italians (57 sources) crackle in it at idle, where only two or three
   * voices are audible, while the Porsche (26) does not. So there the bank keeps exactly as many
   * sources running as are audible and spawns one back, at a random phase and from gain zero, the
   * moment it is wanted. The phase jump is inaudible under a fade from silence; the cost is nil.
   */
  stopSilent?: boolean;
  /**
   * Play the rungs in OUR sampler worklet (public/worklets/sampler.js) instead of one
   * AudioBufferSourceNode each. The Tesla's Chrome 148 clicks on any AudioBufferSourceNode playing at
   * a rate other than exactly 1 (measured in the car, v524: one source, one buffer, straight to the
   * destination, clean at 1.0, crackle at 0.93), and an engine following the revs is nothing but such
   * rates. Requires RPMMixer.prepare(ctx) to have resolved first; falls back to the nodes otherwise.
   */
  sampler?: boolean;
}

/** Equal-power gains for `points` (ascending rpm) at a given rpm. Pure. */
export function bandGains(rpm: number, points: number[]): number[] {
  const g = new Array<number>(points.length).fill(0);
  if (points.length === 0) return g;
  if (rpm <= points[0]) {
    g[0] = 1;
    return g;
  }
  const last = points.length - 1;
  if (rpm >= points[last]) {
    g[last] = 1;
    return g;
  }
  let i = 0;
  while (i < last && points[i + 1] < rpm) i++;
  const t = (rpm - points[i]) / (points[i + 1] - points[i]);
  g[i] = Math.cos(t * Math.PI * 0.5);
  g[i + 1] = Math.sin(t * Math.PI * 0.5);
  return g;
}

/**
 * Playback rate for one rung. Pure, and deliberately the ONLY place this is decided.
 *
 * It is a plain proportion, with no floor: a rung recorded at 1884 rpm played at 0.4 IS a 750 rpm
 * sound, and that is the whole idea of a sampled engine. v369 added a floor so idle rungs recorded
 * above idle would not be "pitched into mud"; what it actually did was freeze the pitch of the
 * sample that owns 70-100 % of the mix from tickover to 2000 rpm, so the engine stopped following
 * the needle exactly where it is heard most. Never add a floor here again.
 */
export function loopRate(rpm: number, recordedRpm: number): number {
  return clamp(rpm / recordedRpm, 0.25, 4);
}

/** ON/OFF set mix for a throttle value. Pure. */
export function throttleMix(throttle: number, floor: number): { on: number; off: number } {
  const t = throttle < 0 ? 0 : throttle > 1 ? 1 : throttle;
  const s = Math.sin(t * Math.PI * 0.5);
  const c = Math.cos(t * Math.PI * 0.5);
  return { on: floor + (1 - floor) * s, off: c };
}

/**
 * THE LOAD AXIS, when the car has a middle to it. Pure.
 *
 * Two recorded loads is a coarse instrument: flat out and completely off the gas. Everything in
 * between — which is how a car is driven almost all of the time — was a blend of those two extremes,
 * and a blend of a scream and an overrun is not what a part-throttle engine sounds like. These banks
 * carry the middle as its own recording (`on_throttle` on the Ferraris, `steady` on the F40 and the
 * SVJ, the `_50` takes on the GT-R), so with one the pedal walks OFF -> MID -> ON through takes the
 * car actually made. Without one the old two-way mix is returned unchanged.
 */
export function loadMix(throttle: number, floor: number, hasMid: boolean): { on: number; mid: number; off: number } {
  const t = throttle < 0 ? 0 : throttle > 1 ? 1 : throttle;
  if (!hasMid) {
    const m = throttleMix(t, floor);
    return { on: m.on, mid: 0, off: m.off };
  }
  if (t <= 0.5) {
    const u = t / 0.5;
    return { off: Math.cos(u * Math.PI * 0.5), mid: Math.sin(u * Math.PI * 0.5), on: floor };
  }
  const u = (t - 0.5) / 0.5;
  return { off: 0, mid: Math.cos(u * Math.PI * 0.5), on: Math.max(floor, Math.sin(u * Math.PI * 0.5)) };
}

/**
 * HOW MUCH OF A LADDER EXISTS AT THIS RPM. Pure.
 *
 * `bandGains` clamps: below a ladder's bottom rung it hands that rung the whole mix, and `loopRate`
 * then plays it at rpm/recordedRpm - floored at 0.25. On the SVJ the MID ladder starts at 4025 rpm,
 * so trundling in first at 1000 with a whisper of throttle played a four-thousand-rpm full-load note
 * at a QUARTER of its speed, two decibels louder than everything around it. That is Marc's "de
 * repente sube el volumen y suena algo sin sentido", and it is worst on the car he drives most: the
 * 458's mid starts at 2975 (rate 0.34) and the 812's at 1312 (0.76, harmless).
 *
 * A ladder is fully itself at or above its bottom rung and gone a quarter below it. In between, the
 * bank crossfades back to the two-way mix it would have had with no mid ladder at all.
 */
export function ladderReach(rpm: number, points: number[]): number {
  if (points.length === 0) return 0;
  const low = points[0];
  if (rpm >= low) return 1;
  return Math.max(0, Math.min(1, (rpm - low * 0.75) / (low * 0.25)));
}

interface Voice {
  loop: LoadedLoop;
  /** index in the sampler worklet's voice table */
  idx: number;
  source: AudioBufferSourceNode | null;
  gain: GainNode | null;
  lastGain: number;
  lastRate: number;
  /** wired into the bus right now: a silent voice is unplugged so it costs nothing */
  live: boolean;
  /** seconds it has been silent (only unplug once the fade is really over) */
  quietFor: number;
}

export class RPMMixer {
  readonly output: GainNode;
  private readonly onBus: GainNode;
  private readonly offBus: GainNode;
  private readonly midBus: GainNode;
  private readonly onVoices: Voice[];
  private readonly offVoices: Voice[];
  private readonly midVoices: Voice[];
  private readonly onPoints: number[];
  private readonly offPoints: number[];
  private readonly midPoints: number[];
  private started = false;
  private lastUpdateAt = 0;
  /** the sampler worklet, when opts.sampler and the module is in */
  private node: AudioWorkletNode | null = null;
  private static ready = new WeakSet<BaseAudioContext>();
  private pending: number[][] = [];

  /** load the sampler module once per context; resolves false where worklets do not exist */
  static async prepare(ctx: BaseAudioContext): Promise<boolean> {
    if (RPMMixer.ready.has(ctx)) return true;
    if (typeof AudioWorkletNode === 'undefined' || !ctx.audioWorklet) return false;
    try {
      await ctx.audioWorklet.addModule('/worklets/sampler.js');
      RPMMixer.ready.add(ctx);
      return true;
    } catch {
      return false;
    }
  }

  constructor(
    private readonly ctx: AudioContext,
    loops: { on: LoadedLoop[]; off: LoadedLoop[]; mid?: LoadedLoop[] },
    private readonly opts: RPMMixerOptions,
  ) {
    this.output = ctx.createGain();
    this.onBus = ctx.createGain();
    this.offBus = ctx.createGain();
    this.midBus = ctx.createGain();
    this.onBus.connect(this.output);
    this.offBus.connect(this.output);
    this.midBus.connect(this.output);
    if (opts.sampler && RPMMixer.ready.has(ctx)) {
      const node = new AudioWorkletNode(ctx, 'rd-sampler', { numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [1] });
      node.connect(this.output);
      this.node = node;
    }
    let idx = 0;
    const mk = (bus: GainNode) => (loop: LoadedLoop): Voice => {
      let gain: GainNode | null = null;
      if (!this.node) {
        gain = ctx.createGain();
        gain.gain.value = 0;
        gain.connect(bus);
      }
      return { loop, idx: idx++, source: null, gain, lastGain: 0, lastRate: 1, live: true, quietFor: 0 };
    };
    const sorted = (l: LoadedLoop[]) => [...l].sort((a, b) => a.recordedRpm - b.recordedRpm);
    this.onVoices = sorted(loops.on).map(mk(this.onBus));
    this.offVoices = sorted(loops.off).map(mk(this.offBus));
    this.midVoices = sorted(loops.mid ?? []).map(mk(this.midBus));
    this.onPoints = this.onVoices.map((v) => v.loop.recordedRpm);
    this.offPoints = this.offVoices.map((v) => v.loop.recordedRpm);
    this.midPoints = this.midVoices.map((v) => v.loop.recordedRpm);
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    const t0 = this.ctx.currentTime + 0.02;
    for (const v of [...this.onVoices, ...this.offVoices, ...this.midVoices]) {
      if (this.node) this.post(v);
      else this.spawn(v, t0, 1);
    }
  }

  /** hand one rung's region to the worklet (a copy: the AudioBuffer stays whole for everything else) */
  private post(v: Voice): void {
    if (!this.node) return;
    const b = v.loop.buffer;
    const sr = b.sampleRate;
    const ls = Math.max(0, Math.round((v.loop.loopStart ?? 0) * sr));
    const leRaw = v.loop.loopEnd && v.loop.loopEnd > (v.loop.loopStart ?? 0) + 0.1 ? Math.round(v.loop.loopEnd * sr) : b.length;
    const le = Math.min(b.length, Math.max(ls + 4, leRaw));
    const data = new Float32Array(le - ls);
    b.copyFromChannel(data, 0, ls);
    const offset = Math.random() * Math.max(1, data.length - 100);
    this.node.port.postMessage({ type: 'voice', id: v.idx, data, rate: 1, gain: 0, offset }, [data.buffer]);
    v.source = null;
  }

  /** one looping source for a voice, started at a random phase so identical-length loops never beat */
  private spawn(v: Voice, at: number, rate: number): void {
    const src = this.ctx.createBufferSource();
    src.buffer = v.loop.buffer;
    src.loop = true;
    const dur = v.loop.buffer.duration;
    const ls = v.loop.loopStart ?? 0;
    const le = v.loop.loopEnd && v.loop.loopEnd > ls + 0.1 ? Math.min(v.loop.loopEnd, dur) : dur;
    if (ls > 0 || le < dur) {
      src.loopStart = ls;
      src.loopEnd = le;
    }
    src.playbackRate.value = rate;
    if (v.gain) src.connect(v.gain);
    src.start(at, ls + Math.random() * Math.max(0.01, le - ls - 0.05));
    v.source = src;
    v.lastRate = rate;
  }

  /**
   * BISECTING THE MIXER ITSELF (Marc, Tesla, v522: clicks with the mixer wired straight to the
   * destination, none from one plain source on the same buffer). 0 normal; 1 no automation at all
   * (gains and rates stay where they are); 2 gains move, rates frozen; 3 rates move, gains frozen.
   */
  debugMode: 0 | 1 | 2 | 3 = 0;

  update(rpm: number, throttle: number): void {
    if (!this.started) return;
    if (this.debugMode === 1) return;
    const now = this.ctx.currentTime;
    const tau = this.opts.tau;
    // ...and the mid ladder only counts where it HAS rungs: under its bottom one the bank walks back
    // to the two-way mix instead of pitching a mid-range note into the cellar.
    const reach = this.midVoices.length ? ladderReach(rpm, this.midPoints) : 0;
    const withMid = loadMix(throttle, this.opts.onMixFloor, this.midVoices.length > 0);
    const mix = reach >= 1 ? withMid : (() => {
      const plain = loadMix(throttle, this.opts.onMixFloor, false);
      return {
        on: plain.on + (withMid.on - plain.on) * reach,
        off: plain.off + (withMid.off - plain.off) * reach,
        mid: withMid.mid * reach,
      };
    })();
    if (this.debugMode !== 3 && !this.node) {
      this.onBus.gain.setTargetAtTime(mix.on, now, tau);
      this.offBus.gain.setTargetAtTime(mix.off, now, tau);
      if (this.midVoices.length) this.midBus.gain.setTargetAtTime(mix.mid, now, tau);
    }
    const dtS = this.lastUpdateAt ? Math.min(0.2, Math.max(0, now - this.lastUpdateAt)) : 0.016;
    this.lastUpdateAt = now;
    if (this.node) {
      // the sampler: one message with every voice's rate and its gain (bus mix folded in);
      // the worklet smooths both per sample, and skips the silent ones itself
      this.pending.length = 0;
      this.collect(this.onVoices, bandGains(rpm, this.onPoints), mix.on, rpm);
      this.collect(this.offVoices, bandGains(rpm, this.offPoints), mix.off, rpm);
      if (this.midVoices.length) this.collect(this.midVoices, bandGains(rpm, this.midPoints), mix.mid, rpm);
      this.node.port.postMessage({ type: 'set', tauG: tau, tauR: tau * 0.6, items: this.pending });
      return;
    }
    this.applySet(this.onVoices, bandGains(rpm, this.onPoints), rpm, now, tau, dtS);
    this.applySet(this.offVoices, bandGains(rpm, this.offPoints), rpm, now, tau, dtS);
    if (this.midVoices.length) this.applySet(this.midVoices, bandGains(rpm, this.midPoints), rpm, now, tau, dtS);
  }

  private collect(voices: Voice[], gains: number[], bus: number, rpm: number): void {
    for (let i = 0; i < voices.length; i++) {
      const v = voices[i];
      const g = gains[i] * bus;
      const rate = loopRate(rpm, v.loop.recordedRpm);
      v.lastGain = g;
      v.lastRate = rate;
      v.live = g > 0.0005;
      this.pending.push([v.idx, this.debugMode === 2 ? -1 : rate, this.debugMode === 3 ? -1 : g]);
    }
  }

  private applySet(voices: Voice[], gains: number[], rpm: number, now: number, tau: number, dtS = 0.016): void {
    for (let i = 0; i < voices.length; i++) {
      const v = voices[i];
      const g = gains[i];
      // A LADDER OF 13 RUNGS KEEPS 13 RESAMPLERS BUSY, and only two of them are ever audible. Chrome
      // still runs a source connected to a gain of zero, so the whole bank (29 sources across both
      // ladders) was being resampled every render quantum for nothing. On a loaded machine that is
      // where the audio thread runs out of time, and a missed quantum is a click: measured on Marc's
      // Mac as a comb at exact multiples of 375 Hz (= 48000/128, the render quantum), up to -13 dBA,
      // absent from an offline mix of the very same ladder, and swinging 25 dB between runs with the
      // same code. A silent voice is unplugged from the bus and plugged back in the moment it is
      // wanted; the source keeps running so its phase never jumps.
      if (g < 0.0005 && v.lastGain < 0.0005) {
        v.quietFor += dtS;
        if (v.live && v.quietFor > 0.35) {
          try { v.gain?.disconnect(); } catch { /* already out */ }
          v.live = false;
          if (this.opts.stopSilent && v.source) {
            try { v.source.stop(); } catch { /* already stopped */ }
            try { v.source.disconnect(); } catch { /* noop */ }
            v.source = null;
          }
        }
      } else {
        v.quietFor = 0;
        if (!v.live) {
          v.gain?.connect(this.busOf(voices));
          v.live = true;
          if (!v.source) this.spawn(v, now, loopRate(rpm, v.loop.recordedRpm));
        }
      }
      if (!v.live) { v.lastGain = g; continue; }
      if (g !== v.lastGain && this.debugMode !== 3 && v.gain) {
        v.gain.gain.setTargetAtTime(g, now, tau);
        v.lastGain = g;
      }
      // Only bother re-pitching audible (or nearly audible) voices.
      if ((g > 0.001 || v.lastGain > 0.001) && this.debugMode !== 2) {
        const rate = loopRate(rpm, v.loop.recordedRpm);
        if (Math.abs(rate - v.lastRate) > 0.0005 && v.source) {
          v.source.playbackRate.setTargetAtTime(rate, now, tau * 0.6);
          v.lastRate = rate;
        }
      }
    }
  }

  /** which bus a voice belongs to (the ON set or the OFF set) */
  private busOf(voices: Voice[]): GainNode {
    return voices === this.onVoices ? this.onBus : voices === this.midVoices ? this.midBus : this.offBus;
  }

  stop(): void {
    if (this.node) {
      try { this.node.port.postMessage({ type: 'clear' }); this.node.disconnect(); } catch { /* noop */ }
      this.node = null;
    }
    for (const v of [...this.onVoices, ...this.offVoices, ...this.midVoices]) {
      try {
        v.source?.stop();
      } catch {
        /* already stopped */
      }
      v.source = null;
    }
    this.started = false;
  }

  get voiceCount(): number {
    return this.onVoices.length + this.offVoices.length + this.midVoices.length;
  }

  /** how many of them are wired into the bus right now */
  get liveCount(): number {
    let n = 0;
    for (const v of [...this.onVoices, ...this.offVoices, ...this.midVoices]) if (v.live) n++;
    return n;
  }

  /** the bottom rung of the ON ladder: the idle, for a diagnostic that wants one plain loop */
  get firstLoop(): LoadedLoop | null { return this.onVoices[0]?.loop ?? null; }

  /** how many sources are actually running (equals voiceCount unless stopSilent; the audible ones in the sampler) */
  get runningCount(): number {
    let n = 0;
    for (const v of [...this.onVoices, ...this.offVoices, ...this.midVoices]) if (this.node ? v.live : v.source) n++;
    return n;
  }

  /** true when the rungs play in the sampler worklet */
  get usesSampler(): boolean { return !!this.node; }
}

function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}
